import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { useState } from "react";
import { handleApiError } from "@/utils/errors";
import { catchError } from "@/lib/errors";
import { toast } from "sonner";
import DotPattern from "@/components/dot-pattern";
import ThemeForm from "@/features/themes/components/form/theme-form";
import { themeFormSchema } from "@/features/themes/components/form/schemas";
import { themesRepo } from "@/lib/repositories";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { useDynamicPageTitle } from "@/hooks/useDynamicPageTitle";
import { useFetchTheme } from "@/features/themes/hooks/useFetchTheme";
import { queryClient } from "@/lib/query-client";
import type { ApiTheme } from "@/features/themes/types";

type ThemesEditFormContentProps = { theme: ApiTheme };

const ThemesEditFormContent = ({ theme }: ThemesEditFormContentProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof themeFormSchema>>({
    resolver: zodResolver(themeFormSchema),
    defaultValues: {
      name: theme.name,
      description: theme.description,
      color: theme.color,
      status: theme.status,
    },
  });

  async function onSubmit(data: z.infer<typeof themeFormSchema>) {
    if (!id) return;
    setIsSubmitting(true);
    const [error, themeData] = await catchError(themesRepo.patch(id, data));
    if (error) toast.error(handleApiError(error).message);
    if (themeData) {
      await queryClient.invalidateQueries({ queryKey: ["themes"] });
      toast.success(t("messages.theme_edit_success"));
      navigate(ROUTES.THEMES);
    }
    setIsSubmitting(false);
  }

  return (
    <ThemeForm
      form={form}
      onSubmit={onSubmit}
      isLoading={isSubmitting}
      theme={theme}
    />
  );
};

const ThemesEditPage = () => {
  const { id } = useParams();
  const { theme, isLoading } = useFetchTheme(id);
  useDynamicPageTitle(theme ? `${theme.reference}` : "");

  return (
    <div className="relative h-full flex flex-col items-center gap-y-3 p-3 py-4 sm:py-10 sm:p-6">
      <DotPattern />
      {isLoading && <Skeleton className="w-full h-137 md:h-80" />}
      {theme && <ThemesEditFormContent theme={theme} />}
    </div>
  );
};

export default ThemesEditPage;
